import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { format } from 'date-fns';
import CircularProgress from '@mui/material/CircularProgress';
import toast from 'react-hot-toast';
import { httpRequest } from 'src/services/httpRequest';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FormControlLabel from '@mui/material/FormControlLabel';
import LoadingOverlay from 'src/components/common/LoadingOverlay';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl';
import InputAdornment from '@mui/material/InputAdornment';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Types
interface ChecklistItem {
    Name: string;
    Checked: boolean;
    IsCustom?: boolean; // Flag to identify custom added items
    id?: string; // Unique identifier for React keys
}

// interface InspectionTypeData {
//     InspectionTypeID: string;
//     InspectionTypeName: string;
//     SubcodeID: number;
//     InspectionCheckList: string[];
// }

/**
 * AssignInspectorModal Component
 * 
 * Modal for front desk managers to assign inspectors to inspection requests
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls whether the modal is open
 * @param {function} props.onClose - Function to call when closing the modal
 * @param {Object} props.applicationData - Object containing application data
 * @param {function} props.onAssignSuccess - Callback after successful assignment
 */
const AssignInspectorModal = ({
    open,
    onClose,
    applicationData,
    onAssignSuccess
}) => {
    // State for form values
    const [inspector, setInspector] = React.useState('');
    const [selectedAvailabilityOption, setSelectedAvailabilityOption] = React.useState(null);
    const [notes, setNotes] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const [inspectors, setInspectors] = React.useState([]);
    const [fetchingInspectors, setFetchingInspectors] = React.useState(false);

    // Checklist related state
    const [checklistItems, setChecklistItems] = React.useState<ChecklistItem[]>([]);
    const [fetchingChecklist, setFetchingChecklist] = React.useState(false);
    const [newChecklistItem, setNewChecklistItem] = React.useState('');
    const [checklistExpanded, setChecklistExpanded] = React.useState(true);

    // Parse suggested availability if provided
    const hasSuggestedAvailability = applicationData?.AvailabilityOptions &&
        Array.isArray(applicationData.AvailabilityOptions) &&
        applicationData.AvailabilityOptions.length > 0;

    // Function to extract time slot ID from time slot string
    const extractTimeSlotID = (timeSlotString) => {
        const timeSlotMap = {
            '09:00 AM - 11:00 AM': 1,
            '11:00 AM - 01:00 PM': 2,
            '02:00 PM - 04:00 PM': 3,
            '04:00 PM - 06:00 PM': 4
        };
        return timeSlotMap[timeSlotString] || 1;
    };

    // Fetch checklist items for the inspection type
    const fetchInspectionTypeChecklist = async (inspectionTypeID) => {
        if (!inspectionTypeID) return;

        setFetchingChecklist(true);
        try {
            const response = await httpRequest(
                'GET',
                `/construction/inspectionTypes/getType/${inspectionTypeID}`
            );

            if (response?.status === 'success' && response.data?.inspectionType) {
                const inspectionType = response.data.inspectionType;
                const checklistArray = inspectionType.InspectionCheckList || [];

                // Convert checklist items to the required format
                const formattedChecklist = checklistArray.map((item, index) => ({
                    Name: item,
                    Checked: false,
                    IsCustom: false,
                    id: `default-${index}`
                }));

                setChecklistItems(formattedChecklist);
            } else {
                console.error('Error fetching inspection type checklist:', response);
                setChecklistItems([]);
            }
        } catch (error) {
            console.error('Error fetching inspection type checklist:', error);
            toast.error('Failed to load inspection checklist');
            setChecklistItems([]);
        } finally {
            setFetchingChecklist(false);
        }
    };

    // Fetch available inspectors based on selected availability option
    const fetchAvailableInspectors = async (availabilityOption) => {
        if (!availabilityOption || !applicationData?.SubcodeNameID) return;

        setFetchingInspectors(true);
        setInspectors([]);
        setInspector('');

        try {
            const formattedDate = format(new Date(availabilityOption.Date), 'MM/dd/yyyy');
            const timeSlotString = availabilityOption.TimeSlots[0];
            const timeSlotID = extractTimeSlotID(timeSlotString);

            const response = await httpRequest(
                'GET',
                `/construction/application/inspector/available-inspectors-for-timeslot?InspectionDate=${formattedDate}&SubcodeID=${applicationData.SubcodeNameID}&TimeSlotID=${timeSlotID}`
            );

            if (response?.status === 'success' && response.data?.inspectors?.available) {
                setInspectors(response.data.inspectors.available);
            } else {
                console.error('Error fetching available inspectors:', response);
                toast.error('No available inspectors found for the selected time slot');
                setInspectors([]);
            }
        } catch (error) {
            console.error('Error fetching available inspectors:', error);
            toast.error('Failed to load available inspectors');
            setInspectors([]);
        } finally {
            setFetchingInspectors(false);
        }
    };

    // Fetch checklist when modal opens and set availability option
    React.useEffect(() => {
        if (open && applicationData?.InspectionTypeID) {
            fetchInspectionTypeChecklist(applicationData.InspectionTypeID);
        }

        // Auto-select the single availability option if available
        if (open && hasSuggestedAvailability && applicationData.AvailabilityOptions.length === 1) {
            const singleOption = applicationData.AvailabilityOptions[0];
            setSelectedAvailabilityOption(singleOption);
            fetchAvailableInspectors(singleOption);
        }
    }, [open, applicationData?.InspectionTypeID, hasSuggestedAvailability]);

    // Handle checkbox change for checklist items
    const handleChecklistItemChange = (itemId, checked) => {
        setChecklistItems(prev =>
            prev.map(item =>
                item.id === itemId ? { ...item, Checked: checked } : item
            )
        );
    };

    // Add new custom checklist item
    const handleAddCustomChecklistItem = () => {
        if (!newChecklistItem.trim()) return;

        const newItem = {
            Name: newChecklistItem.trim(),
            Checked: false,
            IsCustom: true,
            id: `custom-${Date.now()}`
        };

        setChecklistItems(prev => [...prev, newItem]);
        setNewChecklistItem('');
    };

    // Remove custom checklist item
    const handleRemoveCustomChecklistItem = (itemId) => {
        setChecklistItems(prev => prev.filter(item => item.id !== itemId));
    };

    // Handle key press for adding new checklist item
    const handleNewItemKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleAddCustomChecklistItem();
        }
    };

    // Reset form and close modal
    const handleClose = () => {
        setInspector('');
        setSelectedAvailabilityOption(null);
        setNotes('');
        setInspectors([]);
        setChecklistItems([]);
        setNewChecklistItem('');
        setChecklistExpanded(true);
        onClose();
    };

    // Handle form submission
    const handleAssignInspector = async () => {
        if (!inspector || !selectedAvailabilityOption) {
            toast.error('Please select an availability option and inspector');
            return;
        }

        setLoading(true);
        try {
            // Format checklist items for API (remove id, set all Checked to false)
            const formattedChecklistItems = checklistItems.map(item => ({
                Name: item.Name,
                Checked: false,
                IsCustom: item.IsCustom || false
            }));

            const payload = {
                InspectionID: applicationData?.ID,
                InspectorID: inspector,
                AssignTimeSlot: applicationData?.AvailabilityOptions[0]?.TimeSlotID,
                InspectionDate: selectedAvailabilityOption.Date,
                NoteForInspector: notes,
                InspectionCheckList: formattedChecklistItems,
                IsReschedule: false
            };

            const response = await httpRequest('POST', '/construction/application/inspection_assign', payload);

            if (response?.status === 'success') {
                toast.success('Inspector assigned successfully');
                onAssignSuccess();
                handleClose();
            } else {
                toast.error(response?.message || 'Failed to assign inspector');
            }
        } catch (error) {
            console.error('Error assigning inspector:', error);
            toast.error('An error occurred while assigning inspector');
        } finally {
            setLoading(false);
        }
    };

    // Count selected checklist items
    const selectedChecklistCount = checklistItems.filter(item => item.Checked).length;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    margin: { xs: 1, sm: 2 },
                    maxHeight: { xs: '95vh', sm: '90vh' },
                    width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                }
            }}
        >
            {loading && <LoadingOverlay />}
            <DialogTitle
                sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1, sm: 0 },
                    textAlign: { xs: 'center', sm: 'left' },
                    p: { xs: 2, sm: 3 }
                }}
            >
                <AssignmentIndIcon sx={{ mr: { xs: 0, sm: 1 } }} />
                <Typography variant="h6" component="div" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                    Assign Inspector for Inspection Request
                </Typography>
            </DialogTitle>

            <DialogContent sx={{ mt: 2, p: { xs: 2, sm: 3 }, overflow: 'auto' }}>
                {/* Application Info */}
                <Alert severity="info" sx={{ mb: 3 }}>
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" component="div" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                <strong>Application #:</strong> {applicationData?.ApplicationNumber || 'N/A'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" component="div" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                <strong>Subcode:</strong> {applicationData?.SubcodeName || 'Building'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" component="div" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                <strong>Title:</strong> {applicationData?.ApplicationDetails?.ApplicationTitle || 'N/A'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" component="div" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                <strong>Applicant:</strong> {applicationData?.ApplicationDetails?.ApplicantName || 'N/A'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" component="div" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                <strong>Inspection Type:</strong> {applicationData?.InspectionType || 'N/A'}
                            </Typography>
                        </Grid>
                    </Grid>
                </Alert>

                {/* Inspection Checklist Section */}
                <Accordion
                    expanded={checklistExpanded}
                    onChange={(e, isExpanded) => setChecklistExpanded(isExpanded)}
                    sx={{ mb: 4, border: '1px solid', borderColor: 'divider' }}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                            bgcolor: 'primary.alpha12',
                            '&:hover': { bgcolor: 'primary.alpha24' }
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <ChecklistRtlIcon color="primary" />
                            <Typography variant="h6" color="primary">
                                Inspection Checklist
                            </Typography>
                            {checklistItems.length > 0 && (
                                <Chip
                                    size="small"
                                    label={`${selectedChecklistCount}/${checklistItems.length} selected`}
                                    color={selectedChecklistCount > 0 ? 'primary' : 'default'}
                                    variant="outlined"
                                />
                            )}
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        {fetchingChecklist ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
                                <CircularProgress size={24} sx={{ mr: 2 }} />
                                <Typography color="text.secondary">Loading checklist items...</Typography>
                            </Box>
                        ) : (
                            <>
                                {checklistItems.length > 0 ? (
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="subtitle2" gutterBottom color="text.secondary">
                                            Select the checklist items that need to be inspected:
                                        </Typography>
                                        <Paper variant="outlined" sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
                                            <FormGroup>
                                                {checklistItems.map((item) => (
                                                    <Box key={item.id} sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        py: 0.5,
                                                        '&:hover': { bgcolor: 'action.hover' },
                                                        borderRadius: 1,
                                                        px: 1
                                                    }}>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={item.Checked}
                                                                    onChange={(e) => handleChecklistItemChange(item.id, e.target.checked)}
                                                                    size="small"
                                                                />
                                                            }
                                                            label={
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                                                        color: item.Checked ? 'primary.main' : 'text.primary'
                                                                    }}
                                                                >
                                                                    {item.Name}
                                                                    {item.IsCustom && (
                                                                        <Chip
                                                                            size="small"
                                                                            label="Custom"
                                                                            color="secondary"
                                                                            variant="outlined"
                                                                            sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                                                                        />
                                                                    )}
                                                                </Typography>
                                                            }
                                                            sx={{ flex: 1, mr: 1 }}
                                                        />
                                                        {item.IsCustom && (
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => handleRemoveCustomChecklistItem(item.id)}
                                                                sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                ))}
                                            </FormGroup>
                                        </Paper>
                                    </Box>
                                ) : (
                                    <Alert severity="info" sx={{ mb: 3 }}>
                                        No predefined checklist items found for this inspection type.
                                    </Alert>
                                )}

                                {/* Add Custom Checklist Item */}
                                <Box>
                                    <Typography variant="subtitle2" gutterBottom color="text.secondary">
                                        Add additional checklist items if needed:
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Enter additional checklist item..."
                                        value={newChecklistItem}
                                        onChange={(e) => setNewChecklistItem(e.target.value)}
                                        onKeyPress={handleNewItemKeyPress}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        size="small"
                                                        onClick={handleAddCustomChecklistItem}
                                                        disabled={!newChecklistItem.trim()}
                                                        color="primary"
                                                    >
                                                        <AddIcon />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>
                            </>
                        )}
                    </AccordionDetails>
                </Accordion>

                {/* Suggested Availability Section */}
                {hasSuggestedAvailability && (
                    <Alert
                        severity="success"
                        sx={{
                            mb: 3,
                            '& .MuiAlert-message': { width: '100%' }
                        }}
                    >
                        <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                            Availability Option Suggested by Applicant
                        </Typography>

                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 2,
                            mt: 1
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CalendarTodayIcon fontSize="small" color="success" />
                                <Typography variant="body2">
                                    <strong>Date:</strong> {format(new Date(applicationData.AvailabilityOptions[0].Date), 'EEEE, MMMM dd, yyyy')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AccessTimeIcon fontSize="small" color="success" />
                                <Typography variant="body2">
                                    <strong>Time Slots:</strong>
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {applicationData.AvailabilityOptions[0].TimeSlots.map((slot, index) => (
                                        <Chip
                                            key={index}
                                            size="small"
                                            label={slot}
                                            color="success"
                                            variant="outlined"
                                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                    </Alert>
                )}

                <Grid container spacing={{ xs: 2, sm: 3 }}>
                    {/* Inspector Selection */}
                    <Grid item xs={12}>
                        <FormControl fullWidth required>
                            <InputLabel id="inspector-label">Available Inspectors</InputLabel>
                            <Select
                                labelId="inspector-label"
                                value={inspector}
                                label="Available Inspectors"
                                onChange={(e) => setInspector(e.target.value)}
                                disabled={!selectedAvailabilityOption || fetchingInspectors}
                                size="medium"
                            >
                                {fetchingInspectors ? (
                                    <MenuItem disabled>
                                        <CircularProgress size={20} sx={{ mr: 1 }} />
                                        Loading inspectors...
                                    </MenuItem>
                                ) : inspectors.length === 0 ? (
                                    <MenuItem disabled>
                                        No available inspectors for selected time slot
                                    </MenuItem>
                                ) : (
                                    inspectors.map((insp) => (
                                        <MenuItem key={insp.inspectorID} value={insp.inspectorID}>
                                            {insp.name} ({insp.email})
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Display Selected Date and Time */}
                    {selectedAvailabilityOption && (
                        <Grid item xs={12}>
                            <Alert severity="success" sx={{ mb: 2 }}>
                                <Grid container spacing={1}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography
                                            variant="subtitle2"
                                            component="div"
                                            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                                        >
                                            <strong>Selected Date:</strong> {format(new Date(selectedAvailabilityOption.Date), 'EEEE, MMMM dd, yyyy')}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography
                                            variant="subtitle2"
                                            component="div"
                                            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                                        >
                                            <strong>Available Inspectors:</strong> {inspectors.length}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography
                                            variant="subtitle2"
                                            component="div"
                                            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                                        >
                                            <strong>Selected Time Slot:</strong> {selectedAvailabilityOption.TimeSlots.join(', ')}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Alert>
                        </Grid>
                    )}

                    {/* Notes */}
                    <Grid item xs={12}>
                        <TextField
                            label="Notes for Inspector"
                            multiline
                            rows={3}
                            fullWidth
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Add any special instructions or details for the inspector..."
                            size="medium"
                        />
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{
                px: { xs: 2, sm: 3 },
                pb: { xs: 2, sm: 3 },
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1, sm: 2 },
                alignItems: 'stretch'
            }}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    color="secondary"
                    fullWidth={window.innerWidth < 600}
                    sx={{ order: { xs: 2, sm: 1 } }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleAssignInspector}
                    variant="contained"
                    color="primary"
                    disabled={!selectedAvailabilityOption || !inspector}
                    fullWidth={window.innerWidth < 600}
                    sx={{ order: { xs: 1, sm: 2 } }}
                >
                    Assign Inspector
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AssignInspectorModal;